import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UserSummary } from '@bits2schema/shared';
import { api } from '../api/client';

/**
 * Epic 14 (E14-US1) — identita přihlášeného uživatele. Na rozdíl od
 * `I18nProvider`/`ThemeProvider` (klientský stav bez serveru) tenhle provider
 * na startu ověří platnost session cookie přes `GET /auth/me` — 401 je tu
 * legitimní, ne chyba k zobrazení (znamená "zatím nepřihlášen").
 */
type AuthStatus =
  | { kind: 'checking' }
  | { kind: 'loggedOut' }
  | { kind: 'loggedIn'; user: UserSummary };

interface AuthContextValue {
  status: AuthStatus;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Po vytvoření uživatele/změně jména by se dal zavolat pro obnovu — v MVP jen pro konzistenci s ostatními providery. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>({ kind: 'checking' });

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me();
      setStatus({ kind: 'loggedIn', user });
    } catch {
      // 401 (nepřihlášen) i síťová/jiná chyba při startu vedou na přihlašovací
      // obrazovku — bezpečnější než se tvářit jako přihlášený.
      setStatus({ kind: 'loggedOut' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const { user } = await api.login(username, password);
    setStatus({ kind: 'loggedIn', user });
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setStatus({ kind: 'loggedOut' });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ status, login, logout, refresh }), [status, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth musí být uvnitř AuthProvider.');
  return context;
}

/** Zkratka pro komponenty, které fungují jen v přihlášeném stavu (renderují se až po ověření). */
export function useCurrentUser(): UserSummary {
  const { status } = useAuth();
  if (status.kind !== 'loggedIn') throw new Error('useCurrentUser vyžaduje přihlášeného uživatele.');
  return status.user;
}
